import React from "react";
import { styled } from "@mui/system";
import { capitalize } from "lodash";
import GenericTextBlock from "../extra/GenericTextBlock";
import TrophyIcon from "src/icons/TrophyIcon";
import { TIER_REFERRAL_REQUIREMENTS, TIER_REFERRAL_REWARDS } from "../extra/useRafCalculations";

const Wrapper = styled("div")(() => ({
  display: "flex",
  flex: 1,
  width: "100%",
  height: "100%",
  minHeight: "175px",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "16px",
  borderRadius: "20px",
  background: "#FFF",
}));

const HeaderWrapper = styled("div")(() => ({
  display: "flex",
  alignItems: "flex-start",
  gap: "16px",
  alignSelf: "stretch",
}));

const TrophyWrapper = styled("div")(() => ({
  width: "24px",
  height: "24px",
}));

const Header = styled("div")(() => ({
  color: "#002035",
  fontFamily: "Roobert",
  fontSize: "19px",
  fontStyle: "normal",
  fontWeight: 600,
  lineHeight: "23px",
  letterSpacing: " -0.38px",
}));

interface DividerProps {
  background: string;
}
const Divider = styled("div")<DividerProps>(({ background }) => ({
  width: "100%",
  margin: "0px",
  minHeight: "1px",
  background: background,
}));

const Text = styled("div")(() => ({
  alignSelf: "stretch",
  color: " #002035",
  height: "28px",
  fontFamily: "Söhne",
  fontSize: "12px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "14px",
}));

interface TierBreakdownProps {
  tier: "bronze" | "silver" | "gold" | "black";
}

/**
 * A component to display tier breakdown information for different referral program tiers.
 * @param {("bronze" | "silver" | "gold" | "black")} props.tier - The tier for which to display breakdown information.
 */
const TierBreakdown = (props: TierBreakdownProps) => {
  const { tier } = props;
  let text = "";
  let detailsHeaderText = "";
  let tierColor = "";

  switch (tier) {
    case "bronze":
      text = "You are automatically in Bronze tier when you opt in to the Refer A Friend Program.";
      detailsHeaderText = `Receive ${TIER_REFERRAL_REWARDS.BRONZE}% of`;
      tierColor = "#867C6B";
      break;
    case "silver":
      text = `Refer ${TIER_REFERRAL_REQUIREMENTS.SILVER} trading clients`;
      detailsHeaderText = `Receive ${TIER_REFERRAL_REWARDS.SILVER}% of`;
      tierColor = "#C2C3C3";
      break;
    case "gold":
      text = `Refer ${TIER_REFERRAL_REQUIREMENTS.GOLD} trading clients`;
      detailsHeaderText = `Receive ${TIER_REFERRAL_REWARDS.GOLD}% of`;
      tierColor = "#E6D59D";
      break;
    case "black":
      text = `Refer ${TIER_REFERRAL_REQUIREMENTS.BLACK} trading clients`;
      detailsHeaderText = `Receive ${TIER_REFERRAL_REWARDS.BLACK}% of`;
      tierColor = "#16203B";
      break;
    default:
      break;
  }

  return (
    <Wrapper>
      {/* Header */}
      <HeaderWrapper>
        <TrophyWrapper>
          <TrophyIcon color={tierColor} size="24" />
        </TrophyWrapper>
        <Header>{capitalize(tier)}</Header>
      </HeaderWrapper>

      <Divider background={tierColor} />
      <Text>{text}</Text>

      {/* Details */}
      <GenericTextBlock
        header={detailsHeaderText}
        text="Trading fee per trade completed by your referred friends"
      />
    </Wrapper>
  );
};

export default TierBreakdown;
